/**
 * ToolCallCard — 도구 호출(tool-call) 메시지 part 의 기본 렌더러.
 *
 * 책임:
 *  - 도구 이름 + 상태 (in_progress / complete / error) 헤더 표시
 *  - "입력 보기" / "결과 보기" 토글 → JSON 펼침
 *  - 결과가 isError 면 카드 톤을 danger 로
 *
 * 호스트 페이지가 toolName 별 커스텀 카드를 주입하려면 ChatThread 의 `toolUI` prop
 * 으로 매핑을 넘긴다 (예: `suggest_slice` → 슬라이스 미리보기 카드). 매핑에 없는
 * toolName 은 본 기본 카드가 fallback 으로 사용된다.
 *
 * 디자인 토큰 — 모두 `var(--pk-*)` 로 호스트 토큰 기반 theming.
 */
import { useState } from "react"
import type { ToolCallRenderProps } from "./types.ts"

/**
 * 기본 도구 호출 카드.
 *
 * @param props ToolCallRenderProps — toolName / args / result / status / isError
 */
export function ToolCallCard(props: ToolCallRenderProps) {
	const [showInput, setShowInput] = useState(false)
	const [showOutput, setShowOutput] = useState(false)
	const isError = props.isError === true
	const tone = isError
		? "tool-card--error"
		: props.status === "in_progress"
			? "tool-card--running"
			: "tool-card--ok"

	const status = statusLabel(props.status, isError)
	return (
		<div
			className={`tool-card ${tone}`}
			data-tool-name={props.toolName}
			aria-label={`${props.toolName} 도구 호출, ${status}`}
		>
			<header className="tool-card__head">
				<span className="tool-card__name">
					<span className="tool-card__icon" aria-hidden>
						{props.status === "in_progress" ? "⋯" : isError ? "!" : "✓"}
					</span>
					{props.toolName}
				</span>
				<span className="tool-card__status">{status}</span>
			</header>
			<div className="tool-card__body">
				<button
					type="button"
					className="tool-card__toggle"
					onClick={() => setShowInput((v) => !v)}
					aria-expanded={showInput}
				>
					{showInput ? "입력 숨기기" : "입력 보기"}
				</button>
				{showInput && <pre className="tool-card__json">{safeJson(props.args)}</pre>}
				{props.result !== undefined && (
					<>
						<button
							type="button"
							className="tool-card__toggle"
							onClick={() => setShowOutput((v) => !v)}
							aria-expanded={showOutput}
						>
							{showOutput ? "결과 숨기기" : "결과 보기"}
						</button>
						{showOutput && <pre className="tool-card__json">{safeJson(props.result)}</pre>}
					</>
				)}
			</div>
		</div>
	)
}

/** 상태 한국어 라벨. */
function statusLabel(status: ToolCallRenderProps["status"], isError: boolean): string {
	if (isError) return "오류"
	if (status === "in_progress") return "실행 중"
	if (status === "complete") return "완료"
	return "오류"
}

/**
 * unknown 값을 안전하게 JSON 화 — 직렬화 실패 시 String() fallback.
 *
 * @param v 직렬화할 값
 * @returns 보기 좋은 JSON 문자열 (2 space indent)
 */
function safeJson(v: unknown): string {
	try {
		return JSON.stringify(v, null, 2)
	} catch {
		return String(v)
	}
}
